import { useNavigate } from 'react-router';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ShieldCheck, ShieldAlert, LogOut } from 'lucide-react';
import PasskeySettings, { MINIMUM_PASSKEYS } from '@/components/PasskeySettings';

/**
 * Security settings — currently passkey enrollment for superadmins (EIP-305).
 *
 * Deliberately a plain protected route, NOT wrapped in SuperadminRoute. Passkey
 * enrollment has to be reachable by a superadmin who has not finished enrolling,
 * and the enforcement gate bounces those users away from every superadmin page —
 * so hosting enrollment behind that gate is a deadlock. PasskeySettings already
 * self-guards on status.is_superadmin and renders nothing for everyone else.
 *
 * It used to live inside TenantManagementPage's "superadmins" tab, which is
 * exactly the deadlock described above.
 */
export default function SecuritySettingsPage() {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();

  // True when ProtectedRoute is holding this user here (EIP-320). The same flag
  // drives the gate, so the banner cannot claim enrollment is outstanding while
  // the router lets them leave, or vice versa.
  const gated = user?.is_superadmin && user?.passkey_enrollment_required === true;
  const enrolled = user?.passkey_credential_count ?? 0;

  const handleSignOut = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Security</h1>
        <p className="text-muted-foreground">
          Manage the sign-in protections on your account.
        </p>
      </div>

      {gated && (
        <Card className="border-amber-500/50 bg-amber-500/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <ShieldAlert className="w-5 h-5 text-amber-600" />
              Finish setting up your passkeys to continue
            </CardTitle>
            <CardDescription>
              Superadmin accounts need {MINIMUM_PASSKEYS} passkeys before they can
              reach the rest of the platform. You have {enrolled} of{' '}
              {MINIMUM_PASSKEYS}.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <p>
              Two are required rather than one so that a lost or wiped device
              isn&apos;t an account lockout. Use two different authenticators — for
              example this device&apos;s biometrics plus your phone or a security
              key — so a single failure can&apos;t take out both.
            </p>
            {/* Explicit sign-out. The gate pins this user to this page, and the
                sidebar links all bounce straight back here, so without a way out
                the only remaining option is clearing site data by hand. */}
            <Button variant="outline" size="sm" onClick={handleSignOut}>
              <LogOut className="w-4 h-4 mr-2" />
              Sign out
            </Button>
          </CardContent>
        </Card>
      )}

      {user?.is_superadmin ? (
        // refreshUser keeps the cached user payload — which carries the
        // credential count the enrollment gate reads — in step with the server
        // after each registration or removal.
        <PasskeySettings onCredentialsChange={refreshUser} />
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-muted-foreground" />
              Nothing to configure
            </CardTitle>
            <CardDescription>
              There are no additional security settings available for your account.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            Your organization&apos;s administrator manages sign-in policy. To change
            your password, go to your profile settings.
          </CardContent>
        </Card>
      )}
    </div>
  );
}
